/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { withNoTranslate } from "i18n/noTranslate";
import { useEffect, useState } from "react";
import { CheckIcon } from "ui/CheckIcon";
import { Button } from "ui/chakra/components/Button";

const DEFAULT_DURATION_MS = 5000;

// Figma の surface/opposite・text/opposite・border/secondary をモードごとに解決した値。
// 名前の通り画面の地色と反転させるので、ダークモードでは明るいトーストになる。
const PALETTES = {
  light: { surface: "#333", text: "#fff", border: "#f0f0f0" },
  dark: { surface: "#f0f0f0", text: "#000", border: "#141414" },
} as const;

type Props = {
  message: string;
  actionLabel: string;
  onClickAction: () => void;
  durationMs?: number;
  /**
   * コメント欄など特定の領域に重ねるときは absolute（直近の position 指定済み祖先が基準）。
   * ページ全体に対して出すときは fixed。
   */
  position?: "absolute" | "fixed";
  isDarkModeEnabled?: boolean;
};

/**
 * 直前の操作を取り消せることを伝えるトースト。
 *
 * 消えるまで対象の名前を出し続ける必要があるため、消えた後もアンマウントせず
 * 不可視にするだけにしている。そのため 2 回目以降の操作で出し直すには、
 * 呼び出し側が操作ごとに変わる値を key に渡してこのコンポーネントを作り直す。
 */
export const UndoToast: React.FC<Props> = ({
  message,
  actionLabel,
  onClickAction,
  durationMs = DEFAULT_DURATION_MS,
  position = "absolute",
  isDarkModeEnabled = false,
}) => {
  const palette = isDarkModeEnabled ? PALETTES.dark : PALETTES.light;
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, durationMs);
    return () => {
      clearTimeout(timer);
    };
  }, [durationMs]);

  return (
    <div css={containerStyle(isVisible, position, palette)} role="status">
      <div css={itemStyle}>
        <CheckIcon css={iconStyle} />
        <p css={textStyle(palette)} {...withNoTranslate()}>
          {message}
        </p>
      </div>
      <Button
        size="md"
        variant="outline"
        colorScheme="opposite"
        onClick={() => {
          // 押した時点で用は済んでいるので、取り消しの完了を待たずに消す。
          setIsVisible(false);
          onClickAction();
        }}
        css={actionButtonStyle(palette)}
      >
        {actionLabel}
      </Button>
    </div>
  );
};

type Palette = (typeof PALETTES)[keyof typeof PALETTES];

const containerStyle = (
  isVisible: boolean,
  position: "absolute" | "fixed",
  palette: Palette
) =>
  css({
    position,
    left: "16px",
    right: "16px",
    bottom: "16px",
    // left/right を張ったうえで max-width と auto マージンを効かせて中央に置く。
    maxWidth: "600px",
    marginInline: "auto",
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    padding: "8px 12px",
    borderRadius: "8px",
    backgroundColor: palette.surface,
    boxShadow:
      "0px 0px 1px rgba(24, 24, 27, 0.3), 0px 16px 24px rgba(24, 24, 27, 0.1)",
    opacity: isVisible ? 1 : 0,
    visibility: isVisible ? "visible" : "hidden",
    transition: "all 0.3s ease-in-out",
  });

const itemStyle = css({
  flex: "1 0 0",
  minWidth: 0,
  display: "flex",
  alignItems: "flex-start",
  columnGap: "12px",
});

const iconStyle = css({
  flexShrink: 0,
  path: {
    fill: "#38a169",
  },
});

const textStyle = (palette: Palette) =>
  css({
    flex: "1 0 0",
    minWidth: 0,
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 700,
    color: palette.text,
    wordBreak: "break-word",
  });

const actionButtonStyle = (palette: Palette) =>
  css({
    flexShrink: 0,
    minWidth: "40px",
    paddingTop: "2px",
    paddingBottom: "2px",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 500,
    color: palette.text,
    borderColor: palette.border,
  });
